import React, { useEffect, useMemo, useRef, useState } from "react";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import BdrCheckbox from "../../../../components/BdrCheckbox/BdrCheckbox";
import BdrSelectPopup from "../../../../components/BdrInput/BdrSelectPopup";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import "./createEventModal.css";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import CloseIcon from "@mui/icons-material/Close";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  GET_ALL_USERS,
  GET_CLIENT_BY_ID,
  GET_ZONE_WEATHER_FOR_RANGE,
} from "../../../../utils/GraphQL/queries";
import WeatherChip from "../../../../components/WeatherChip/WeatherChip";
import { buildWeatherLookup, weatherForJob } from "../../../../utils/weatherZoneJoin";
import {
  CREATE_CALENDAR_ITEM,
  UPDATE_QUOTE_INVOICE,
  JOIN_CALENDAR_CHAIN,
} from "../../../../utils/GraphQL/mutations";
import ClientSearchInput from "../ClientSearchInput/ClientSearchInput";
import ChainSelector from "../ChainSelector/ChainSelector";
import RepeatFields from "../shared/RepeatFields/RepeatFields";
import PlaceholderCleanupModal from "../PlaceholderCleanupModal/PlaceholderCleanupModal";
import PayPeriodMoveConfirmModal, { isNeedsPeriodConfirm } from "../PayPeriodMoveConfirmModal/PayPeriodMoveConfirmModal";
import EventDateTimeInput from "../EventDateTimeInput/EventDateTimeInput";
import BdrInput from "../../../../components/BdrInput/BdrInput";
import NumberStepper from "../NumberStepper/NumberStepper";
import { buildSplitBetween } from "../../../../utils/calendarSplitBetween";
import AdminBDRSpinner from "../AdminBDRSpinner/AdminBDRSpinner";
import { priceStrFormatter } from "../../../../utils/formatters/priceStrFormatter";
import BAGroupTimeFormat from "../../../../utils/formatters/BAGroupTimeFormat";
import { extractCalNotes } from "../../../../utils/formatters/extractCalNotes";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import { hasAnyRole, hasRole } from '../../../../utils/permissions';
import Auth from "../../../../utils/auth";
import {
  PERSONAL_LABEL_MAX,
  toLocalNoonMsStr,
  FREQ_WEEKLY,
  repeatInputFrom,
} from "../../../../utils/calendarPersonalEvents";
import SpecialRefinisherPayBlock, {
  buildSpecialRefinisherPayInput,
  validateSpecialRefinisherPay,
} from "../../../../components/SpecialRefinisherPayBlock/SpecialRefinisherPayBlock";
import {
  WEEKDAY_LABELS,
  generateDayList,
  buildWorkDaysPayload,
  previewDayTimes,
} from "../../../../utils/workDayBuilder";

const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
const ONE_HOUR_MS = 60 * 60 * 1000;

const CreateEventModal = ({
  open,
  onClose,
  onEventCreated,
  passedInvoiceId,
  passedClientId,
  passedStartMs,
  passedEndMs,
  // Personal ("SIX") events, 2026-09-11: the type the selector opens on.
  // "personal" comes from the SIX buttons; everything else opens on "job".
  initialEventType = "job",
}) => {
  const { handleSnack } = useSnackbar();
  // Only admins may create personal events (server-enforced too); the option
  // is hidden for everyone else so a seeded "personal" can never leak.
  const isAdmin = useMemo(() => hasRole(Auth.getPermissions(), "admin"), []);
  // Form state
  // Calendar-event-types: "job" (default) | "go_by" | "vacation" | "personal".
  const [eventType, setEventType] = useState("job");
  const [note, setNote] = useState("");
  // Personal ("SIX") fields: required label (<= 80 chars) and the All Day
  // switch (Google-Calendar language). Off = start AND end times required.
  const [label, setLabel] = useState("");
  const [allDay, setAllDay] = useState(true);
  // Repeat rule (2026-09-20). Independent of All Day — all four combinations
  // are valid. `on: false` sends no rule at all, i.e. a one-off.
  const [repeat, setRepeat] = useState({
    on: false,
    freq: FREQ_WEEKLY,
    interval: "1",
    endsOn: "",
  });
  // Chain-link selector state. `chainAfter` holds { targetId, targetChainGroupId }
  // from ChainSelector — resolved into the actual chainGroupId at submit time.
  const [chainAfter, setChainAfter] = useState(null);
  // Available Sooner (2026-10-07): job / go_by only. See the reset in the
  // eventType handler — this leaks exactly like placeholderMode would.
  const [availableSooner, setAvailableSooner] = useState(false);
  const [clientId, setClientId] = useState("");
  const [clientName, setClientName] = useState("");
  // Weather-by-zone (2026-06-22): the selected client's zone + the forecast
  // for the chosen start day, shown as a scheduling forewarning chip.
  const [clientZone, setClientZone] = useState(null);
  const [zoneWeather, setZoneWeather] = useState(null);
  const [selectedDocId, setSelectedDocId] = useState("");
  const [selectedDocType, setSelectedDocType] = useState(""); // 'invoice' | 'quote'
  // Tentative placeholder mode (job only): block off a date with NO invoice/
  // quote. Sends isPlaceholder:true; hides the doc dropdown + SRP/split blocks.
  const [placeholderMode, setPlaceholderMode] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [startMs, setStartMs] = useState("");
  const [endMs, setEndMs] = useState("");
  const [assignedUserIds, setAssignedUserIds] = useState([]);
  const [priorityNote, setPriorityNote] = useState("");
  const [locked, setLocked] = useState(false);
  const [users, setUsers] = useState([]);
  const [splitMin, setSplitMin] = useState("");
  const [splitMax, setSplitMax] = useState("");
  const [srpEnabled, setSrpEnabled] = useState(false);
  const [srpAmount, setSrpAmount] = useState(null);
  const [srpPayType, setSrpPayType] = useState(null);
  // Dirty-gate: only send specialRefinisherPay when the user touched the SRP
  // block after selecting a doc. When omitted, createCalendarItem inherits
  // the override FRESH from the linked invoice/quote server-side — strictly
  // better than sending this modal's point-in-time doc-list copy, which can
  // be stale and (via the upgrade-in-place dedupe path) wipe a live override.
  const srpDirtyRef = useRef(false);
  const [error, setError] = useState("");
  // Per-field validation messages for the personal ("SIX") path, keyed by
  // field: label | date | start | end. A failed submit shows them all at once
  // and keeps the modal open; each clears on its own field's next edit.
  const [fieldErrors, setFieldErrors] = useState({});
  const clearFieldError = (key) =>
    setFieldErrors((prev) =>
      prev[key] ? { ...prev, [key]: undefined } : prev
    );
  const [submitting, setSubmitting] = useState(false);
  const [loadingClient, setLoadingClient] = useState(false);
  const [clientNotes, setClientNotes] = useState([]);
  const [gateCode, setGateCode] = useState("");
  // Multi-day work-day checklist — empty unless end date > start date.
  // checkedKeys preserves user's check edits across regenerations so
  // toggling a day stays toggled when times shift.
  const [workDayList, setWorkDayList] = useState([]);
  const [checkedKeys, setCheckedKeys] = useState(() => new Set());
  // Cleanup-modal handoff after a create-with-doc (offers to clear the
  // customer's OTHER tentative placeholders).
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleanupExcludeId, setCleanupExcludeId] = useState("");
  // Pay-period move confirm (Option 2): set when re-submitting an existing
  // approved job (upgrade-in-place) would re-bucket its paycheck.
  const [periodMove, setPeriodMove] = useState(null);

  const [getAllUsers] = useLazyQuery(GET_ALL_USERS);
  const [getClientById] = useLazyQuery(GET_CLIENT_BY_ID);
  const [getZoneWeather] = useLazyQuery(GET_ZONE_WEATHER_FOR_RANGE);
  const [createCalendarItem] = useMutation(CREATE_CALENDAR_ITEM);
  const [joinCalendarChain] = useMutation(JOIN_CALENDAR_CHAIN);
  const [updateQuoteInvoice] = useMutation(UPDATE_QUOTE_INVOICE);

  // Fetch active users on open
  useEffect(() => {
    if (open) {
      // Seed the type selector. A non-admin can never land on "personal".
      setEventType(
        initialEventType === "personal" && isAdmin ? "personal" : "job"
      );
      fetchUsers();
      // If passed IDs, prefill
      if (passedClientId) {
        fetchClientData(passedClientId, passedInvoiceId);
      }
      // Prefill times if passed
      if (passedStartMs) setStartMs(passedStartMs);
      if (passedEndMs) setEndMs(passedEndMs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Reset form when closing
  useEffect(() => {
    if (!open) {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const resetForm = () => {
    setEventType("job");
    setNote("");
    setLabel("");
    setAllDay(true);
    setRepeat({ on: false, freq: FREQ_WEEKLY, interval: "1", endsOn: "" });
    setClientId("");
    setClientName("");
    setClientZone(null);
    setZoneWeather(null);
    setSelectedDocId("");
    setSelectedDocType("");
    setPlaceholderMode(false);
    // Both were missing, and every host keeps this modal mounted: a ticked
    // Available Sooner reopened pre-ticked on the next create, and a chosen
    // "is after" target survived hidden (the selector only renders once a
    // start is picked) and chained the NEXT event to it on submit.
    setAvailableSooner(false);
    setChainAfter(null);
    setInvoices([]);
    setQuotes([]);
    setStartMs("");
    setEndMs("");
    setAssignedUserIds([]);
    setPriorityNote("");
    setLocked(false);
    setSplitMin("");
    setSplitMax("");
    setSrpEnabled(false);
    setSrpAmount(null);
    setSrpPayType(null);
    srpDirtyRef.current = false;
    setError("");
    setFieldErrors({});
    setSubmitting(false);
    setLoadingClient(false);
    setClientNotes([]);
    setGateCode("");
    setWorkDayList([]);
    setCheckedKeys(new Set());
    setCleanupOpen(false);
    setCleanupExcludeId("");
  };

  /*
   * An "is after" target belongs to the DAY it was picked on: ChainSelector
   * lists only that day's events. Moving the start to ANOTHER day hid the
   * pick (the selector shows "— none —") but kept it in state, and submit then
   * joined the new event to a chain on a different day — where the server
   * cascades the crew across the whole chain. Same day, new time: kept.
   * Day compared by local y/m/d, never a Duration (DST-safe).
   */
  const chainDayRef = useRef(null);
  useEffect(() => {
    const n = parseInt(startMs, 10);
    const d = Number.isFinite(n) ? new Date(n) : null;
    const day = d ? `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}` : null;
    if (chainDayRef.current !== null && day !== chainDayRef.current) {
      setChainAfter(null);
    }
    chainDayRef.current = day;
  }, [startMs]);

  // Regenerate work-day list when start/end date span crosses midnight.
  // Preserve user's check edits across regenerations (by dateKey). New
  // entries inherit the Sat/Sun auto-skip default.
  useEffect(() => {
    const list = generateDayList(startMs, endMs);
    setWorkDayList(list);
    setCheckedKeys((prev) => {
      const next = new Set();
      for (const d of list) {
        if (prev.has(d.key)) {
          next.add(d.key);
        } else if (!d.isWeekend) {
          next.add(d.key);
        }
      }
      return next;
    });
  }, [startMs, endMs]);

  // Weather forewarning: when a client (with a zone) + a start day are chosen,
  // pull that day's per-zone forecast. Re-runs when the zone or start day
  // changes. Failure is swallowed (chip just doesn't render).
  useEffect(() => {
    if (!open || !clientZone) {
      setZoneWeather(null);
      return undefined;
    }
    // Forecast the chosen start day; before one is picked, default to TODAY so
    // selecting a client immediately shows that client's-area weather.
    const parsed = parseInt(startMs, 10);
    const ms = Number.isNaN(parsed) ? Date.now() : parsed;
    // Widen the window by a day on each side so the Central-day join is robust
    // across the UTC<->Central boundary at the edges of the range.
    const startWindow = String(ms - 86400000);
    const endWindow = String(ms + 86400000);
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getZoneWeather({
          variables: { startMs: startWindow, endMs: endWindow },
          fetchPolicy: "cache-first",
        });
        if (!cancelled) setZoneWeather(data?.getZoneWeatherForRange || null);
      } catch (err) {
        console.error("Error fetching zone weather (create event):", err);
        if (!cancelled) setZoneWeather(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientZone, startMs]);

  const fetchUsers = async () => {
    try {
      const { data } = await getAllUsers({ fetchPolicy: "network-only" });
      const allUsers = data?.getAllUsers || [];
      // Refinishers are the default field staff, so they should always appear
      // at the top of the assignee list. Other eligible users (admins) sort
      // below so they're still available when a non-refinisher needs to be
      // assigned to a job. Alphabetical within each bucket.
      const activeUsers = allUsers
        .filter((u) => u.active !== false && hasAnyRole(u.permission, ['refinisher', 'admin']))
        .sort((a, b) => {
          const aRef = hasAnyRole(a.permission, ['refinisher']) ? 0 : 1;
          const bRef = hasAnyRole(b.permission, ['refinisher']) ? 0 : 1;
          if (aRef !== bRef) return aRef - bRef;
          const aName = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase();
          const bName = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase();
          return aName.localeCompare(bName);
        });
      setUsers(activeUsers);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  const fetchClientData = async (cId, preselectedInvoiceId) => {
    setLoadingClient(true);
    try {
      const { data } = await getClientById({
        variables: {
          clientId: cId,
          currentTime: Date.now().toString(),
        },
        fetchPolicy: "network-only",
      });

      const client = data?.getClientById;
      if (client) {
        setClientId(client._id);
        setClientName(
          `${client.firstName || ""} ${client.lastName || ""}`.trim()
        );
        // Weather zone (server-derived from zipCode) for the forewarning chip.
        setClientZone(client.zone || null);

        // Set client notes
        setClientNotes(client.clientNotes || []);
        setGateCode(client.gateCode || "");

        // Set invoices (filter to unpaid, sort by dateCreated newest first)
        const clientInvoices = (client.invoices || [])
          .filter((inv) => !inv.invoicePaid)
          .sort((a, b) => {
            const aMs = parseInt(a.dateCreated) || 0;
            const bMs = parseInt(b.dateCreated) || 0;
            return bMs - aMs;
          });
        setInvoices(clientInvoices);

        // Set quotes (sort by dateCreated newest first)
        const clientQuotes = [...(client.quotes || [])]
          .sort((a, b) => {
            const aMs = parseInt(a.dateCreated) || 0;
            const bMs = parseInt(b.dateCreated) || 0;
            return bMs - aMs;
          });
        setQuotes(clientQuotes);

        // If a specific invoice was passed, select it and prefill dates
        if (preselectedInvoiceId) {
          setSelectedDocId(preselectedInvoiceId);
          setSelectedDocType("invoice");
          const matchedInv = clientInvoices.find(
            (inv) => inv.id === preselectedInvoiceId
          );
          if (matchedInv?.jobScheduledFor) {
            prefillDatesFromInvoice(matchedInv.jobScheduledFor);
          }
        }
      }
    } catch (err) {
      console.error("Error fetching client:", err);
    }
    setLoadingClient(false);
  };

  const prefillDatesFromInvoice = (jobScheduledForMs) => {
    const ms = parseInt(jobScheduledForMs);
    if (isNaN(ms)) return;

    const dt = new Date(ms);
    // Set to 8:00 AM of that day
    const start = new Date(
      dt.getFullYear(),
      dt.getMonth(),
      dt.getDate(),
      8,
      0,
      0
    );
    // Set to 2:00 PM of that day (6 hour window)
    const end = new Date(
      dt.getFullYear(),
      dt.getMonth(),
      dt.getDate(),
      14,
      0,
      0
    );

    setStartMs(start.getTime().toString());
    setEndMs(end.getTime().toString());
  };

  const handleClientSelected = (client) => {
    if (!client?._id) return;
    setClientId(client._id);
    setClientName(
      `${client.firstName || ""} ${client.lastName || ""}`.trim()
    );
    // Fetch client details to get invoices
    fetchClientData(client._id);
  };

  const handleClearClient = () => {
    setClientId("");
    setClientName("");
    setClientZone(null);
    setZoneWeather(null);
    setSelectedDocId("");
    setSelectedDocType("");
    setInvoices([]);
    setQuotes([]);
    setSplitMin("");
    setSplitMax("");
    setClientNotes([]);
    setGateCode("");
  };

  // Invoices and quotes as one list with group labels. `header: true` rows
  // render but cannot be picked, and the arrow keys step over them — the same
  // thing `<option disabled>` did in the native list.
  const docOptions = [
    ...(invoices.length > 0 ? [{ header: true, label: "— Invoices —" }] : []),
    ...invoices.map((inv) => ({
      value: `invoice:${inv.id}`,
      label: `#${inv.invoiceNum} — ${priceStrFormatter(inv.invoiceTotal)}${
        inv.jobScheduledFor
          ? ` (Scheduled: ${BAGroupTimeFormat(inv.jobScheduledFor, true, true)})`
          : ""
      }`,
    })),
    ...(quotes.length > 0 ? [{ header: true, label: "— Quotes —" }] : []),
    ...quotes.map((q) => ({
      value: `quote:${q.id}`,
      label: `#${q.quoteNum} — ${priceStrFormatter(q.quoteTotal)}`,
    })),
  ];

  const handleDocChange = (e) => {
    const val = e.target.value;
    if (!val) {
      setSelectedDocId("");
      setSelectedDocType("");
      return;
    }

    // Value format: "invoice:id" or "quote:id"
    const [type, ...idParts] = val.split(":");
    const id = idParts.join(":");
    setSelectedDocId(id);
    setSelectedDocType(type);

    // Prefill splitBetween + specialRefinisherPay from selected doc
    const doc =
      type === "invoice"
        ? invoices.find((inv) => inv.id === id)
        : quotes.find((q) => q.id === id);
    const split = doc?.splitBetween || [];
    setSplitMin(split[0] != null ? String(split[0]) : "");
    setSplitMax(split.length > 1 ? String(split[split.length - 1]) : "");
    const docSrp = doc?.specialRefinisherPay;
    setSrpEnabled(!!docSrp?.enabled);
    setSrpAmount(docSrp?.amount || null);
    setSrpPayType(docSrp?.payType || null);
    // Prefill from the selected doc = not a user edit; picking a different
    // doc also discards any touch made against the previous selection.
    srpDirtyRef.current = false;

    // If an invoice is selected, prefill dates from jobScheduledFor
    if (type === "invoice") {
      const matchedInv = invoices.find((inv) => inv.id === id);
      if (matchedInv?.jobScheduledFor) {
        prefillDatesFromInvoice(matchedInv.jobScheduledFor);
      }
    }
  };

  const handleStartTimeReturn = (mill) => {
    if (typeof mill !== "string") return;
    const newStart = parseInt(mill);
    if (isNaN(newStart)) return;

    setStartMs(mill);

    // If end time exists, sync the day and validate
    if (endMs) {
      const oldEnd = parseInt(endMs);
      if (!isNaN(oldEnd)) {
        // Move end time to same day as new start, keeping end's hours/minutes
        const startDt = new Date(newStart);
        const endDt = new Date(oldEnd);
        const syncedEnd = new Date(
          startDt.getFullYear(), startDt.getMonth(), startDt.getDate(),
          endDt.getHours(), endDt.getMinutes(), endDt.getSeconds()
        ).getTime();

        if (newStart >= syncedEnd) {
          // Start is after or equal to end — push end to start + 2hrs
          setEndMs(String(newStart + TWO_HOURS_MS));
        } else {
          setEndMs(String(syncedEnd));
        }
      }
    }
  };

  const handleEndTimeReturn = (mill) => {
    if (typeof mill !== "string") return;
    const newEnd = parseInt(mill);
    if (isNaN(newEnd)) return;

    if (startMs) {
      const curStart = parseInt(startMs);
      if (!isNaN(curStart) && newEnd <= curStart) {
        // End is before or equal to start — shift start to end - 1hr
        setStartMs(String(newEnd - ONE_HOUR_MS));
        setEndMs(mill);
        return;
      }
    }

    setEndMs(mill);
  };

  // Personal ("SIX") create — its own path so none of the client / doc /
  // assignee / work-day rules below apply. Payload mirrors the mobile dialog:
  //   { eventType:"personal", label, startMs, isAllDay, endMs? (timed only),
  //     note? (only when non-empty) }
  // All-day startMs is sent as an EXPLICIT local-noon anchor (the server
  // re-normalizes to Central noon) so a DST day can never shift the bucket.
  // Every problem at once, keyed by field, so one submit surfaces all of them
  // instead of making the user discover them one failed click at a time.
  const validatePersonal = () => {
    const errs = {};
    const trimmedLabel = label.trim();
    if (!trimmedLabel) errs.label = "Please enter a label";
    else if (trimmedLabel.length > PERSONAL_LABEL_MAX)
      errs.label = `Label must be ${PERSONAL_LABEL_MAX} characters or fewer`;

    if (allDay) {
      if (!startMs) errs.date = "Please select a date";
    } else {
      if (!startMs) errs.start = "Please select a start time";
      if (!endMs) errs.end = "Please select an end time";
      // Only meaningful once both exist; otherwise the "required" message
      // above is the real problem and this would double up on it.
      else if (startMs && parseInt(endMs) <= parseInt(startMs))
        errs.end = "End time must be after start time";
    }
    return errs;
  };

  const handleSubmitPersonal = async () => {
    setError("");
    const errs = validatePersonal();
    setFieldErrors(errs);
    // Return early and stay open — closing here would discard what was typed
    // with no explanation.
    if (Object.keys(errs).length > 0) return;

    const trimmedLabel = label.trim();
    setSubmitting(true);
    const input = {
      eventType: "personal",
      label: trimmedLabel,
      startMs: allDay ? toLocalNoonMsStr(startMs) : startMs,
      isAllDay: allDay,
    };
    if (!allDay) input.endMs = endMs;
    if (note.trim()) input.note = note.trim();
    // The server derives the weekday / day-of-month from the event's own date,
    // so only the client-settable half of the rule goes over the wire.
    const recurrence = repeatInputFrom(repeat, { hadRule: false });
    if (recurrence) input.recurrence = recurrence;

    try {
      await createCalendarItem({ variables: { input } });
      handleSnack("Personal Event Created", 2500);
      onEventCreated?.();
    } catch (err) {
      console.error("Error creating personal calendar item:", err);
      const msg = err.message || "Failed to create personal event";
      setError(msg);
      handleSnack(msg, 3000);
    }
    setSubmitting(false);
  };

  // periodConfirmed is only true when the pay-period confirm modal retries.
  // `=== true` guards against the Create button passing a click event arg.
  const handleSubmit = async (periodConfirmed) => {
    setError("");

    if (eventType === "personal") {
      await handleSubmitPersonal();
      return;
    }

    // Vacation has no client; other types require one.
    if (eventType !== "vacation" && !clientId) {
      setError("Please select a client");
      return;
    }
    // Go By events have no invoice/quote; job events require one UNLESS this is
    // a tentative placeholder (intentional no-doc date hold).
    if (eventType === "job" && !placeholderMode && !selectedDocId) {
      setError("Please select an invoice or quote");
      return;
    }
    if (eventType === "vacation") {
      if (!Array.isArray(assignedUserIds) || assignedUserIds.length !== 1) {
        setError("Select exactly one refinisher for the Time Off entry");
        return;
      }
    }
    if (!startMs) {
      setError("Please select a start time");
      return;
    }

    // Final validation: end before start
    if (endMs && startMs && parseInt(endMs) < parseInt(startMs)) {
      setError("End time cannot be before start time");
      handleSnack("End time cannot be before start time", 3000);
      return;
    }

    // Build multi-day workDays payload when the day checklist surfaced
    // and has 2+ days checked. If only 1 day is checked (or the span is
    // single-day and the list never appeared), send empty workDays —
    // server treats that as the single-day path.
    const checkedInOrder = workDayList.filter((d) => checkedKeys.has(d.key));
    const workDaysPayload = buildWorkDaysPayload(checkedInOrder, startMs, endMs);

    if (workDayList.length > 0 && checkedInOrder.length === 0) {
      setError("Select at least one work day");
      setSubmitting(false);
      return;
    }

    setSubmitting(true);

    const input = {
      eventType,
      startMs,
      endMs: endMs || undefined,
      workDays: workDaysPayload,
      assignedUserIds,
      priorityNotes: priorityNote.trim() ? [priorityNote.trim()] : [],
      locked,
    };

    // Special Refinisher Pay override (job events only; N/A for a no-doc
    // placeholder — there's no invoice/quote to attach pay to).
    // Dirty-gated: only sent when the user touched the SRP block. When
    // omitted, the server inherits the override fresh from the linked
    // invoice/quote — safer than sending this modal's doc-list snapshot.
    if (eventType === "job" && !placeholderMode && srpDirtyRef.current) {
      const srpValidationError = validateSpecialRefinisherPay(srpEnabled, srpAmount);
      if (srpValidationError) {
        setError(srpValidationError);
        handleSnack(srpValidationError, 4000);
        setSubmitting(false);
        return;
      }
      input.specialRefinisherPay = buildSpecialRefinisherPayInput(
        srpEnabled,
        srpAmount,
        srpPayType
      );
    }
    // Vacation has no client; omit the field so server doesn't choke on empty
    // string. Other types pass the selected clientId.
    if (eventType !== "vacation") {
      input.clientId = clientId;
    }
    // Only for the types the server accepts it on. Sending it otherwise would
    // be silently dropped, so the saved event would differ from the form.
    if (["job", "go_by"].includes(eventType) && availableSooner) {
      input.availableSooner = true;
    }
    if (eventType === "job") {
      if (placeholderMode) {
        // Tentative placeholder: no invoice/quote. Server allows a doc-less
        // job only when isPlaceholder is true.
        input.isPlaceholder = true;
        // Placeholder Min/Max (2026-10-29): no doc to hold the number, so it
        // rides on the calendar item itself. The server keeps it only while
        // the item is a placeholder and carries it onto the doc on promote.
        const placeholderSplit = buildSplitBetween(splitMin, splitMax);
        if (placeholderSplit.length > 0) {
          input.placeholderSplitBetween = placeholderSplit;
        }
      } else if (selectedDocType === "invoice") {
        input.invoiceId = selectedDocId;
      } else {
        input.quoteId = selectedDocId;
      }
    } else if (eventType === "go_by" || eventType === "vacation") {
      // Optional text note; no invoice/quote for either Go By or Vacation.
      if (note.trim()) input.note = note.trim();
    }

    // Chain join is deferred to AFTER create (see joinCalendarChain block
    // below). Setting chainGroupId directly on the create input tripped a
    // self-heal race: server rebalance runs after the create, finds a
    // single-member group, and clears chainGroupId. The dedicated
    // `joinCalendarChain` mutation does both writes atomically.

    // Pay-period guard retry: set only when the confirm modal re-invoked submit.
    if (periodConfirmed === true) input.confirmPeriodMove = true;

    try {
      const createResult = await createCalendarItem({
        variables: { input },
      });
      const createdId = createResult?.data?.createCalendarItem?.id;

      // Chain join: atomic server-side mutation sets chainGroupId on BOTH
      // the new event and the target in one updateMany, so rebalance never
      // sees a singleton. Skips vacation (server rejects it defensively too).
      if (createdId && chainAfter?.targetId && eventType !== "vacation") {
        try {
          await joinCalendarChain({
            variables: {
              sourceId: createdId,
              targetId: chainAfter.targetId,
            },
          });
        } catch (chainErr) {
          console.error(
            "[CreateEvent] joinCalendarChain failed:",
            chainErr?.message || chainErr
          );
          handleSnack(
            "Event saved, but chaining failed — retry from Edit.",
            3500
          );
        }
      }

      // Save splitBetween to the linked invoice/quote if values were provided
      const minVal = parseInt(splitMin);
      const maxVal = parseInt(splitMax);
      const newSplit = [];
      if (!isNaN(minVal)) newSplit.push(minVal);
      if (!isNaN(maxVal) && maxVal !== minVal) newSplit.push(maxVal);

      if (selectedDocId && newSplit.length > 0) {
        const isInvoice = selectedDocType === "invoice";
        const doc = isInvoice
          ? invoices.find((inv) => inv.id === selectedDocId)
          : quotes.find((q) => q.id === selectedDocId);
        if (doc) {
          await updateQuoteInvoice({
            variables: {
              id: doc.id,
              isInvoice,
              discountPercent: doc.discountPercent || "0",
              subtotal: doc.subtotal || "0",
              total: isInvoice ? doc.invoiceTotal || "0" : doc.quoteTotal || "0",
              splitBetween: newSplit,
            },
          });
        }
      }

      handleSnack("Event Created", 2500);
      // If a doc was attached (not a placeholder), the customer may have other
      // tentative dates that this booking supersedes — offer cleanup before
      // closing. Placeholder creates (and go_by/vacation) just close: creating
      // a placeholder doesn't supersede anything.
      if (eventType === "job" && !placeholderMode && clientId) {
        setCleanupExcludeId(createdId || "");
        setCleanupOpen(true);
      } else {
        onEventCreated?.();
      }
    } catch (err) {
      // Re-submitting an existing approved job to a new date that re-buckets its
      // paycheck across a settled period — confirm + retry.
      if (isNeedsPeriodConfirm(err)) {
        setPeriodMove({
          newMs: startMs,
          onConfirm: () => handleSubmit(true),
        });
        setSubmitting(false);
        return;
      }
      console.error("Error creating calendar item:", err);
      const msg = err.message || "Failed to create event";
      setError(msg);
      handleSnack(msg, 3000);
    }

    setSubmitting(false);
  };

  return (
    <>
    <BdrModal
      open={open}
      onClose={onClose}
      variant="panel"
      size={clientId ? "lg" : "md"}
      showHeader={false}
      ariaLabel="Create event"
      /* raised: opened from the day / week / day-view modals */
      layer="raised"
      /* A walkthrough may close this (tours/tourModals.js). Every host's
         onClose only sets its open state false; closing runs resetForm. */
      tourTag="create-event"
    >
      <div className="create-event-content bdr-dark-placeholder">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3>{eventType === "personal" ? "Personal Event" : "Create Calendar Event"}</h3>
          <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </div>

        <div className={`event-modal-layout${clientId ? "" : " event-modal-layout--single"}`}>
        <div className="event-modal-main">
        {/* Event Type selector — Job | Go By | Vacation. */}
        <div className="create-event-field">
          <span className="create-event-field-label">Event Type</span>
          <BdrSelectPopup
            id="create-event-type"
            className="bdr-input bdr-select-trigger"
            value={eventType}
            /* Personal stays admin-only here, as it has been in all three
               places that gate it — this option, the SIX seed, and the
               server. */
            options={[
              { value: "job", label: "Job" },
              { value: "go_by", label: "Go By (no invoice, no job form)" },
              { value: "vacation", label: "Time Off / Vacation" },
              ...(isAdmin
                ? [{ value: "personal", label: "Personal (SIX)" }]
                : []),
            ]}
            onChange={(e) => {
              const next = e.target.value;
              setEventType(next);
              // Placeholder mode is job-only; reset on any type change so a
              // stale toggle never leaks into go_by/vacation (or a fresh job).
              setPlaceholderMode(false);
              // Same leak, same fix: Available Sooner is job/go_by-only, and
              // the server strips it for anything else. Clearing it here means
              // the form never SHOWS a state the server will not keep.
              setAvailableSooner(false);
              // Which fields are required is per-type, so a banner raised
              // under one type is meaningless under the next.
              setFieldErrors({});
              setError("");
              // Switching types clears fields that don't apply. Server rejects
              // cross-type payloads defensively — this is just UX hygiene.
              if (next === "go_by") {
                setSelectedDocId("");
                setSelectedDocType("");
                setSplitMin("");
                setSplitMax("");
                setSrpEnabled(false);
                setSrpAmount(null);
                setSrpPayType(null);
                srpDirtyRef.current = false;
              } else if (next === "vacation" || next === "personal") {
                setSelectedDocId("");
                setSelectedDocType("");
                setSplitMin("");
                setSplitMax("");
                setSrpEnabled(false);
                setSrpAmount(null);
                setSrpPayType(null);
                srpDirtyRef.current = false;
                setPriorityNote("");
                setLocked(false);
                // Clear client — neither vacation nor personal has a client.
                // User-selected client is dropped from state so a later switch
                // back to Job requires re-selecting (explicit).
                setClientId("");
                setClientName("");
                // Vacation requires exactly one assigned user; personal is
                // creator-stamped server-side. Either way, start clean.
                setAssignedUserIds([]);
                setChainAfter(null);
              } else {
                // Back to "job" — clear the Go By note.
                setNote("");
              }
              if (next !== "personal") {
                setLabel("");
                setAllDay(true);
                // A repeat rule is personal-only; leaving it set would send a
                // rule the server strips, and re-selecting Personal would
                // silently restore a schedule the user had moved away from.
                setRepeat({ on: false, freq: FREQ_WEEKLY, interval: "1", endsOn: "" });
              }
            }}
          />
        </div>

        {/* Personal ("SIX") label — the whole title of the event */}
        {eventType === "personal" && (
          <div className="create-event-field">
            {/* BdrInput renders the error banner itself, in the field stack.
                `error` takes the MESSAGE, not a boolean — passing `true` drew
                a second, empty red banner above this one. */}
            <BdrInput
              type="text"
              label="Label"
              name="label"
              onChange={(e) => {
                setLabel(e.target.value);
                clearFieldError("label");
              }}
              value={label}
              error={fieldErrors.label}
              maxLength={PERSONAL_LABEL_MAX}
              autoFocus
            />
          </div>
        )}

        {/* Tentative Placeholder toggle — job only. Blocks off a candidate date
            with NO invoice/quote (sends isPlaceholder:true). */}
        {eventType === "job" && (
          <div className="create-event-field">
            <label className="create-event-field-label">Tentative Placeholder</label>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Switch
                checked={placeholderMode}
                onChange={(e) => {
                  const on = e.target.checked;
                  setPlaceholderMode(on);
                  if (on) {
                    // A placeholder carries no doc / SRP — clear them. Min/Max
                    // is deliberately KEPT (2026-10-29): it now rides on the
                    // placeholder itself, so a number typed or prefilled from a
                    // doc before flipping the toggle is still wanted.
                    setSelectedDocId("");
                    setSelectedDocType("");
                    setSrpEnabled(false);
                    setSrpAmount(null);
                    setSrpPayType(null);
                    srpDirtyRef.current = false;
                  }
                }}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#00e676" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#00e676" },
                }}
              />
              <span style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.85rem" }}>
                {placeholderMode
                  ? "Tentative — no invoice/quote required (blocks the date)"
                  : "Off — a normal job needs an invoice or quote"}
              </span>
            </div>
          </div>
        )}

        {/* Client Search — hidden for Vacation + Personal (no client) */}
        {eventType !== "vacation" && eventType !== "personal" && (
          <div className="create-event-field">
            <span className="create-event-field-label">Client</span>
            {clientId ? (
              <div className="create-event-client-display">
                <span className="create-event-client-name">{clientName}</span>
                {(() => {
                  const { day, fetchedMs } = weatherForJob(
                    buildWeatherLookup(zoneWeather),
                    clientZone,
                    startMs || String(Date.now())
                  );
                  return day ? (
                    <span style={{ marginLeft: 8 }}>
                      <WeatherChip day={day} fetchedMs={fetchedMs} size={14} />
                    </span>
                  ) : null;
                })()}
                <button
                  className="create-event-client-clear"
                  onClick={handleClearClient}
                >
                  Change
                </button>
              </div>
            ) : loadingClient ? (
              <AdminBDRSpinner message="Loading..." />
            ) : (
              <ClientSearchInput
                returnResults={handleClientSelected}
                setWidth="100%"
              />
            )}
          </div>
        )}

        {/* Invoice/Quote Dropdown — hidden for Go By + placeholder mode */}
        {clientId && eventType === "job" && !placeholderMode && (
          <div className="create-event-field">
            {/* `popup`: this list grows with the client's history — a long one
                ran off the top of the screen as a native <select>, whose list
                is an OS popup CSS cannot cap. See BdrSelectPopup.jsx. */}
            <BdrInput
              label="Invoice / Quote"
              name="invoiceQuote"
              popup
              placeholder="-- Select Invoice or Quote --"
              options={docOptions}
              value={selectedDocId ? `${selectedDocType}:${selectedDocId}` : ""}
              onChange={handleDocChange}
            />
          </div>
        )}

        {/* Personal ("SIX"): All Day switch. On = a single date (noon anchor,
            no end); off = start AND end times below. */}
        {eventType === "personal" && (
          <div className="create-event-field">
            <label className="create-event-field-label">All Day</label>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Switch
                checked={allDay}
                onChange={(e) => {
                  const on = e.target.checked;
                  setAllDay(on);
                  // Switching to all-day drops the end; the date is kept.
                  if (on) setEndMs("");
                  // All-day needs a date; timed needs a start AND an end. A
                  // banner from the other mode no longer describes anything.
                  setFieldErrors({});
                }}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#00e676" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#00e676" },
                }}
              />
              <span style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.85rem" }}>
                {allDay ? "All day — pick a date" : "Timed — pick a start and end"}
              </span>
            </div>
          </div>
        )}

        {eventType === "personal" && allDay ? (
          /* All-day personal: date only. The emitted ms carries a noon clock
             from defaultTime; submit re-anchors to local noon regardless. */
          <div className="create-event-field">
            {fieldErrors.date && (
              <div className="bdr-alert bdr-alert--danger bdr-alert--inline">
                {fieldErrors.date}
              </div>
            )}
            <div className="create-event-row">
              <EventDateTimeInput
                value={startMs}
                onChange={(ms) => {
                  if (typeof ms === "string") setStartMs(ms);
                  clearFieldError("date");
                }}
                label="Date:"
                hideTime
                defaultTime={{ hour24: 12, minute: 0 }}
              />
            </div>
          </div>
        ) : (
          <>
            {/* Start Time */}
            <div className="create-event-field">
              {fieldErrors.start && (
                <div className="bdr-alert bdr-alert--danger bdr-alert--inline">
                  {fieldErrors.start}
                </div>
              )}
              <div className="create-event-row">
                <EventDateTimeInput
                  value={startMs}
                  onChange={(ms) => {
                    handleStartTimeReturn(ms);
                    clearFieldError("start");
                    // The end error may have been "must be after start" —
                    // moving start can resolve it, so re-check on submit
                    // rather than leaving a message that is no longer true.
                    clearFieldError("end");
                  }}
                  label="Start:"
                />
              </div>
            </div>

            {/* End Time */}
            <div className="create-event-field">
              {fieldErrors.end && (
                <div className="bdr-alert bdr-alert--danger bdr-alert--inline">
                  {fieldErrors.end}
                </div>
              )}
              <div className="create-event-row">
                <EventDateTimeInput
                  value={endMs}
                  onChange={(ms) => {
                    handleEndTimeReturn(ms);
                    clearFieldError("end");
                  }}
                  label="End:"
                />
              </div>
            </div>
          </>
        )}

        {/* Available Sooner — its own row directly under the date fields.
            job / go_by only: both are visits to a named customer that could be
            pulled forward. The helper says what ticking it actually does,
            because "available sooner" on its own does not say WHERE it shows
            up. */}
        {["job", "go_by"].includes(eventType) && (
          <div className="create-event-row" data-tour="create-event-avail-sooner">
            <BdrCheckbox
              name="availableSooner"
              label="Available Sooner"
              helper="Lists this customer on the Tasks page so an earlier opening can be offered to them."
              checked={availableSooner}
              onChange={(e) => setAvailableSooner(e.target.checked)}
            />
          </div>
        )}

        {/* Repeat rule (2026-09-20) — rendered after the date fields because
            the pattern is DERIVED from the chosen date: there is no weekday or
            day-of-month picker, since "starts Monday, repeats Tuesdays" has no
            sensible first occurrence. */}
        {eventType === "personal" && (
          <RepeatFields
            value={repeat}
            onChange={setRepeat}
            dateMs={startMs}
            disabled={submitting}
          />
        )}

        {/* Is After chain selector — hidden for Vacation (§6.12) + Personal */}
        {eventType !== "vacation" && eventType !== "personal" && startMs && (
          <ChainSelector
            dayStartMs={startMs}
            currentEventId={null}
            chainGroupId={null}
            selectedAfterId={chainAfter?.targetId || null}
            onChange={setChainAfter}
            onBreakChain={() => setChainAfter(null)}
            eventType={eventType}
          />
        )}

        {/* Work Days — only shown when end date > start date (never for personal) */}
        {eventType !== "personal" && workDayList.length > 0 && (
          <div className="create-event-field">
            <span className="create-event-field-label">
              Work Days ({Array.from(checkedKeys).length} selected)
            </span>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                padding: "8px 10px",
                background: "rgba(255,255,255,0.05)",
                borderRadius: 6,
                border: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              <div style={{ fontSize: 11, color: "#b0b0cc", marginBottom: 2 }}>
                Weekends auto-skipped. Middle days run 8 AM – 6 PM. First/last day use your chosen start/end times.
              </div>
              {(() => {
                const checkedInOrder = workDayList.filter((d) => checkedKeys.has(d.key));
                return workDayList.map((d) => {
                  const isChecked = checkedKeys.has(d.key);
                  const idxInChecked = checkedInOrder.findIndex((e) => e.key === d.key);
                  const isFirst = isChecked && idxInChecked === 0;
                  const isLast = isChecked && idxInChecked === checkedInOrder.length - 1;
                  const dateLabel = `${WEEKDAY_LABELS[d.weekday]} ${d.month + 1}/${d.day}`;
                  const timeLabel = isChecked
                    ? previewDayTimes(isFirst, isLast, startMs, endMs)
                    : "skipped";
                  let roleLabel = "";
                  if (isChecked) {
                    if (checkedInOrder.length === 1) roleLabel = "[only day]";
                    else if (isFirst) roleLabel = "[first]";
                    else if (isLast) roleLabel = "[last]";
                  }
                  return (
                    <BdrCheckbox
                      key={d.key}
                      className={`create-event-day-row${
                        isChecked ? "" : " is-off"
                      }`}
                      checked={isChecked}
                      onChange={(e) => {
                        setCheckedKeys((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(d.key);
                          else next.delete(d.key);
                          return next;
                        });
                      }}
                      label={
                        <>
                          <span className="create-event-day-date">
                            {dateLabel}
                          </span>
                          <span className="create-event-day-time">
                            {timeLabel}
                          </span>
                          {roleLabel && (
                            <span className="create-event-day-role">
                              {roleLabel}
                            </span>
                          )}
                        </>
                      }
                    />
                  );
                });
              })()}
            </div>
          </div>
        )}

        {/* Assigned Users — hidden for Personal (creator-stamped server-side) */}
        {eventType !== "personal" && (
        <div className="create-event-field">
          <span className="create-event-field-label">Assigned To</span>
          <div className="create-event-user-list">
            {users.map((user) => (
              <BdrCheckbox
                key={user._id}
                className="create-event-user-row"
                checked={assignedUserIds.includes(user._id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setAssignedUserIds((prev) => [...prev, user._id]);
                  } else {
                    setAssignedUserIds((prev) =>
                      prev.filter((id) => id !== user._id)
                    );
                  }
                }}
                label={
                  <>
                    <span
                      className="create-event-user-dot"
                      style={{ background: user.defaultColor || "#888" }}
                    />
                    <span className="create-event-user-name">
                      {user.firstName} {user.lastName}
                    </span>
                  </>
                }
              />
            ))}
          </div>
        </div>
        )}

        {/* Refinisher Min/Max — every job, placeholders included (2026-10-29:
            a placeholder keeps the number on the calendar item). Hidden for
            Go By / Vacation / Personal. */}
        {eventType === "job" && (
          <div className="create-event-field">
            <span className="create-event-field-label">Refinishers (Min / Max)</span>
            <div style={{ display: "flex", gap: 14 }}>
              <NumberStepper
                label="Min"
                value={splitMin}
                onChange={(val) => setSplitMin(String(val))}
                min={1}
                max={99}
              />
              <NumberStepper
                label="Max"
                value={splitMax}
                onChange={(val) => setSplitMax(String(val))}
                min={1}
                max={99}
              />
            </div>
          </div>
        )}

        {/* Priority Note — hidden for Vacation + Personal */}
        {eventType !== "vacation" && eventType !== "personal" && (
          <div className="create-event-field">
            <BdrInput
              
              type="text"
              label="Priority Note (optional)"
              name="priorityNote"
              onChange={(e) => setPriorityNote(e.target.value)}
              value={priorityNote}
              multiline
              rows={2}
            />
          </div>
        )}

        {/* Go By / Vacation / Personal Note field */}
        {(eventType === "go_by" || eventType === "vacation" || eventType === "personal") && (
          <div className="create-event-field">
            <BdrInput
              
              type="text"
              label={
                eventType === "go_by"
                  ? "Go By Note (optional)"
                  : eventType === "personal"
                    ? "Note (optional)"
                    : "Time Off Note (optional)"
              }
              name="note"
              onChange={(e) => setNote(e.target.value)}
              value={note}
              multiline
              rows={2}
            />
          </div>
        )}

        {/* Lock Job — hidden for Vacation + Personal (no AI reassignment to lock against) */}
        {eventType !== "vacation" && eventType !== "personal" && (
          <div className="create-event-field">
            <label className="create-event-field-label">Lock Job</label>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Switch
                checked={locked}
                onChange={(e) => setLocked(e.target.checked)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#00e676" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#00e676" },
                }}
              />
              <span style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.85rem" }}>
                {locked ? "Locked — AI will not reassign" : "Unlocked"}
              </span>
            </div>
          </div>
        )}

        {/* Special Refinisher Pay — job events only (not placeholders) */}
        {eventType === "job" && !placeholderMode && (() => {
          const selectedDoc =
            selectedDocType === "invoice"
              ? invoices.find((inv) => inv.id === selectedDocId)
              : quotes.find((q) => q.id === selectedDocId);
          const srpItems =
            selectedDoc?.invoiceItems || selectedDoc?.quoteItems || [];
          return (
            <div className="create-event-field">
              <SpecialRefinisherPayBlock
                enabled={srpEnabled}
                setEnabled={(v) => {
                  srpDirtyRef.current = true;
                  setSrpEnabled(v);
                }}
                amount={srpAmount}
                setAmount={(v) => {
                  srpDirtyRef.current = true;
                  setSrpAmount(v);
                }}
                payType={srpPayType}
                setPayType={(v) => {
                  srpDirtyRef.current = true;
                  setSrpPayType(v);
                }}
                theme="dark"
                items={srpItems}
              />
            </div>
          );
        })()}

        {/* Error */}
        {error && (
          <div className="bdr-alert bdr-alert--danger bdr-alert--start">
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="create-event-actions">
          <BdrButton onClick={onClose}>Cancel</BdrButton>
          {/* solid: this modal is variant="panel", the saturated
              --bdr-grad-panel, where a tinted fill muddies in (§6b). */}
          <BdrButton
            variant="confirm-solid"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Creating..." : eventType === "personal" ? "Create" : "Create Event"}
          </BdrButton>
        </div>
        </div>{/* end event-modal-main */}

        {/* Notes Sidebar */}
        {clientId && (
          <div className="event-modal-notes-sidebar">
            {/* Cal Notes */}
            <div className="event-notes-cal-section">
              <h4 className="event-notes-cal-title">Cal Notes</h4>
              {gateCode && gateCode.trim() !== "" && (
                <div className="event-notes-cal-item" style={{ userSelect: "text" }}>
                  Gate Code: {gateCode}
                </div>
              )}
              {(() => {
                const calNotes = extractCalNotes(clientNotes);
                return calNotes.length > 0 ? (
                  <div className="event-notes-cal-list">
                    {calNotes.map((note, idx) => (
                      <div key={idx} className="event-notes-cal-item" style={{ userSelect: "text" }}>
                        {note}
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="event-notes-muted">No calendar notes</span>
                );
              })()}
            </div>

            <div className="event-notes-divider" />

            {/* Client Notes */}
            <div className="event-notes-list">
              {clientNotes.length > 0 ? (
                clientNotes.map((n, idx) => (
                  <div key={n._id || idx} className="event-note-item">
                    <div className="event-note-date">
                      {BAGroupTimeFormat(n.dateCreated, true, true, true)}
                    </div>
                    <div className="event-note-body" style={{ userSelect: "text" }}>
                      {n.note}
                    </div>
                  </div>
                ))
              ) : (
                <span className="event-notes-muted">No client notes</span>
              )}
            </div>
          </div>
        )}
        </div>{/* end event-modal-layout */}
      </div>
    </BdrModal>
    <PlaceholderCleanupModal
      open={cleanupOpen}
      clientId={clientId}
      excludeEventId={cleanupExcludeId}
      onClose={() => {
        setCleanupOpen(false);
        onEventCreated?.();
      }}
    />
    <PayPeriodMoveConfirmModal
      open={!!periodMove}
      oldMs={periodMove?.oldMs}
      newMs={periodMove?.newMs}
      busy={submitting}
      onCancel={() => {
        setPeriodMove(null);
        setSubmitting(false);
      }}
      onConfirm={() => {
        setSubmitting(true);
        periodMove?.onConfirm?.();
      }}
    />
    </>
  );
};

export default CreateEventModal;
